import { CookieSettingsLink } from "@/lib/marketing-analytics/cookie-settings-link";
import { BRAND_LOGO_TITLE, MARKETING_LOGO_ALT } from "@/lib/marketing-seo";
import { BrandLogoMark } from "@v1/ui/brand-logo-mark";
import { ArrowUpRight } from "lucide-react";
import { useTranslations } from "next-intl";
import type { ReactNode } from "react";
import { SpotifyMark, YouTubeMark } from "../hero-social-brand-icons";
import { H } from "../hero-tokens";

/**
 * HERO — SECTION 6 + 7: social link cards ("Schau rein, hör rein.") plus the
 * footer bar (dark logo + copyright left, legal links right). 1:1 with the
 * prototype (Hausverwaltung Hero.dc.html, lines 2111–2150).
 */

const CARD_SHADOW =
  "0 1px 0 rgba(255,255,255,0.7) inset, 0 16px 34px -26px rgba(11,15,25,0.22)";

const cardBaseStyle = {
  flex: 1,
  display: "flex",
  alignItems: "center",
  gap: 15,
  padding: "18px 20px",
  background: "#fff",
  border: "1px solid #ECEEF1",
  borderRadius: 16,
  textDecoration: "none",
  boxShadow: CARD_SHADOW,
  transition:
    "border-color .18s ease, transform .18s ease, box-shadow .18s ease",
} as const;

/** Neutral tile so the two brand marks are the only color on the card. */
const iconWrapStyle = {
  width: 48,
  height: 48,
  borderRadius: 14,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  background: "#F4F4F5",
  border: "1px solid rgba(11,15,25,0.06)",
} as const;

const cardTitleStyle = {
  display: "block",
  fontSize: 15.5,
  fontWeight: 600,
  color: H.ink,
} as const;

const cardSubStyle = {
  display: "block",
  fontSize: 13,
  color: "rgba(11,15,25,0.55)",
  marginTop: 1,
} as const;

const arrowStyle = {
  marginLeft: "auto",
  color: "rgba(11,15,25,0.32)",
  display: "inline-flex",
  flexShrink: 0,
} as const;

export function HeroSocialFooter({
  showSocialSection = true,
}: {
  readonly showSocialSection?: boolean;
}): ReactNode {
  const t = useTranslations("marketing.heroSocialFooter");
  return (
    <footer
      style={{ maxWidth: 1240, margin: "92px auto 0", padding: "0 28px" }}
    >
      {showSocialSection ? (
        <>
          <div style={{ textAlign: "center" }}>
            <h2
              style={{
                fontFamily: H.display,
                fontWeight: 400,
                fontSize: 34,
                lineHeight: 1.1,
                letterSpacing: "-0.018em",
                color: H.ink,
                margin: "14px 0 0",
              }}
            >
              {t("schauReinHoerRein")}
            </h2>
          </div>

          <div
            style={{
              display: "flex",
              gap: 16,
              maxWidth: 680,
              margin: "30px auto 0",
            }}
          >
            <a
              className="mkhero-social"
              href="https://www.youtube.com/@hausverwalter-ai"
              target="_blank"
              rel="noreferrer"
              style={cardBaseStyle}
            >
              <span style={iconWrapStyle}>
                <YouTubeMark />
              </span>
              <span style={{ minWidth: 0 }}>
                <span style={cardTitleStyle}>{t("youtube")}</span>
                <span style={cardSubStyle}>{t("softwareLiveErleben")}</span>
              </span>
              <span style={arrowStyle}>
                <ArrowUpRight width={18} height={18} strokeWidth={2} />
              </span>
            </a>

            <a
              className="mkhero-social"
              href="https://open.spotify.com/show/033k4UZU6bCuhiABtyxxFx"
              target="_blank"
              rel="noreferrer"
              style={cardBaseStyle}
            >
              <span style={iconWrapStyle}>
                <SpotifyMark />
              </span>
              <span style={{ minWidth: 0 }}>
                <span style={cardTitleStyle}>{t("podcast")}</span>
                <span style={cardSubStyle}>{t("prozesseHausverwaltung")}</span>
              </span>
              <span style={arrowStyle}>
                <ArrowUpRight width={18} height={18} strokeWidth={2} />
              </span>
            </a>
          </div>
        </>
      ) : null}

      <div
        style={{
          marginTop: 46,
          paddingTop: 22,
          borderTop: "1px solid #ECEEF1",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span
            role="img"
            aria-label={MARKETING_LOGO_ALT}
            title={BRAND_LOGO_TITLE}
            style={{ display: "inline-flex", flexShrink: 0 }}
          >
            <BrandLogoMark width={24} height={24} variant="dark" />
          </span>
          <span style={{ fontSize: 13, color: "rgba(11,15,25,0.5)" }}>
            {t("k2026HausverwalterAi")}
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 22 }}>
          <a
            className="mkhero-footlink"
            aria-label={t("karriereseiteOeffnen")}
            href="/careers"
            style={{
              fontSize: 12.5,
              color: "rgba(11,15,25,0.5)",
              textDecoration: "none",
            }}
          >
            {t("karriere")}
          </a>
          <a
            className="mkhero-footlink"
            aria-label={t("datenschutzerklaerungOeffnen")}
            href="/datenschutz"
            style={{
              fontSize: 12.5,
              color: "rgba(11,15,25,0.5)",
              textDecoration: "none",
            }}
          >
            {t("datenschutz")}
          </a>
          <a
            className="mkhero-footlink"
            aria-label={t("impressumOeffnen")}
            href="/impressum"
            style={{
              fontSize: 12.5,
              color: "rgba(11,15,25,0.5)",
              textDecoration: "none",
            }}
          >
            {t("impressum")}
          </a>
          <CookieSettingsLink variant="marketing" />
        </div>
      </div>
    </footer>
  );
}
